import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import axiosInstance from "../axiosInstance";
import {
  createSetting,
  deleteSetting,
  listSettings,
  updateSetting,
} from "./settingsService";

const empty = {
  currency_code: "",
  name: "",
  symbol: "",
  units_per_base: "1.000000",
  is_active: true,
};

export default function CurrencySettings() {
  const [items, setItems] = useState([]);
  const [baseCurrency, setBaseCurrency] = useState("USD");
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = useCallback(async () => {
    const [currencies, company] = await Promise.all([
      listSettings("currencies"),
      axiosInstance.get("settings/company/"),
    ]);
    setItems(currencies);
    setBaseCurrency(company.data?.default_currency_code || "USD");
  }, []);

  useEffect(() => {
    load().catch(() => toast.error("Could not load currencies."));
  }, [load]);

  const editingBase = useMemo(
    () => String(form.currency_code || "").toUpperCase() === baseCurrency,
    [form.currency_code, baseCurrency]
  );

  const save = async (event) => {
    event.preventDefault();
    const payload = {
      ...form,
      currency_code: String(form.currency_code || "").toUpperCase(),
      name: String(form.name || "").toUpperCase(),
      units_per_base: editingBase ? "1.000000" : form.units_per_base,
    };
    try {
      if (editId) await updateSetting("currencies", editId, payload);
      else await createSetting("currencies", payload);
      toast.success("Currency saved. Linked payment methods now use this rate.");
      setEditId(null);
      setForm(empty);
      await load();
    } catch (error) {
      toast.error(
        Object.values(error.response?.data || {}).flat().join(" ") ||
          "Could not save currency."
      );
    }
  };

  return (
    <section className="settings-panel">
      <div className="settings-panel__head">
        <div>
          <h2>Currencies</h2>
          <p>
            This is the single exchange-rate master. Payment methods inherit
            their rate from the currency selected here.
          </p>
        </div>
      </div>

      <div className="payment-rate-guidance">
        <i className="bi bi-info-circle"></i>
        <div>
          <strong>Current base currency: {baseCurrency}</strong>
          <p>
            The base currency is always 1.000000. For another currency enter
            how many units equal 1 {baseCurrency}. Example: if 1 {baseCurrency}
            = 18.50 ZAR, enter 18.500000 for ZAR.
          </p>
        </div>
      </div>

      <form className="settings-form-grid" onSubmit={save}>
        <label>
          <span>Currency code</span>
          <input
            maxLength="3"
            value={form.currency_code}
            onChange={(e) => setForm({ ...form, currency_code: e.target.value.toUpperCase() })}
            required
          />
        </label>
        <label>
          <span>Currency name</span>
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value.toUpperCase() })}
            required
          />
        </label>
        <label>
          <span>Symbol</span>
          <input
            value={form.symbol || ""}
            onChange={(e) => setForm({ ...form, symbol: e.target.value })}
          />
        </label>
        <label>
          <span>Exchange rate</span>
          <input
            type="number"
            min="0.000001"
            step="0.000001"
            value={editingBase ? "1.000000" : form.units_per_base}
            readOnly={editingBase}
            onChange={(e) => setForm({ ...form, units_per_base: e.target.value })}
            required
          />
          <small>
            {editingBase
              ? `${baseCurrency} is the base currency, so its rate is locked at 1.000000.`
              : `Units of this currency equal to 1 ${baseCurrency}.`}
          </small>
        </label>
        <label>
          <span>Active</span>
          <div className="settings-check-row">
            <input
              type="checkbox"
              checked={Boolean(form.is_active)}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
            <small>{form.is_active ? "YES" : "NO"}</small>
          </div>
        </label>
        <div className="settings-form-actions">
          <button type="submit"><i className="bi bi-check2-circle"></i>{editId ? " UPDATE" : " ADD"}</button>
          {editId && (
            <button type="button" className="secondary" onClick={() => { setEditId(null); setForm(empty); }}>
              CANCEL
            </button>
          )}
        </div>
      </form>

      <div className="settings-table-wrap">
        <table>
          <thead><tr><th>Currency</th><th>Name</th><th>Symbol</th><th>Rate</th><th>Status</th><th>Action</th></tr></thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id}>
                <td>{item.currency_code}{item.is_base_currency ? " (BASE)" : ""}</td>
                <td>{item.name}</td>
                <td>{item.symbol || "—"}</td>
                <td>{item.is_base_currency ? "1.000000" : item.units_per_base}</td>
                <td>{item.is_active ? "ACTIVE" : "INACTIVE"}</td>
                <td>
                  <button type="button" className="settings-icon-action" title="Edit" onClick={() => {
                    setEditId(item.id);
                    setForm({ ...item, units_per_base: item.is_base_currency ? "1.000000" : item.units_per_base });
                  }}><i className="bi bi-pencil-square"></i></button>
                  {!item.is_base_currency && (
                    <button type="button" className="settings-icon-action danger" title="Delete" onClick={async () => {
                      if (window.confirm("Delete this currency?")) {
                        try {
                          await deleteSetting("currencies", item.id);
                          await load();
                        } catch (error) {
                          toast.error(error.response?.data?.detail || "Currency is in use. Deactivate it instead.");
                        }
                      }
                    }}><i className="bi bi-trash3"></i></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
